import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Badge } from './ui/badge';
import { bookedDates } from '../mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BookingCalendar = () => {
  const [date, setDate] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const isDateBooked = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return bookedDates.includes(dateStr);
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    
    if (isDateBooked(selectedDate)) {
      toast({
        title: "Date Currently Unavailable",
        description: "This date is already booked, but we'll check if we can accommodate you. Please submit a request.",
      });
    } else {
      const dateStr = selectedDate?.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      toast({
        title: "Date Available!",
        description: `${dateStr} is available. Click 'Request Quote' to proceed.`,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Your Event Date</CardTitle>
        <CardDescription>Dates in grey are already booked</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center space-y-6">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            disabled={(date) => {
              // Only disable past dates, not booked dates
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
            modifiers={{
              booked: (date) => isDateBooked(date)
            }}
            modifiersClassNames={{
              booked: 'opacity-50 bg-gray-100'
            }}
            className="rounded-md border"
          />
          
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-700 rounded"></div>
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-300 rounded"></div>
              <span>Unavailable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-black rounded"></div>
              <span>Available</span>
            </div>
          </div>

          {date && !isDateBooked(date) && (
            <div className="w-full">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <p className="text-center font-semibold text-green-800 mb-4">
                    ✓ {date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} is available!
                  </p>
                  <Button 
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    disabled={submitting}
                    onClick={async () => {
                      setSubmitting(true);
                      try {
                        const bookingData = {
                          selectedDate: date.toISOString().split('T')[0],
                          isBooked: false
                        };
                        await axios.post(`${API}/bookings`, bookingData);
                        toast({
                          title: "Booking Inquiry Sent!",
                          description: "We'll contact you shortly to confirm availability and pricing.",
                        });
                      } catch (error) {
                        console.error('Error submitting booking:', error);
                        toast({
                          title: "Error",
                          description: "Failed to submit booking inquiry. Please try again.",
                          variant: "destructive"
                        });
                      }
                      setSubmitting(false);
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Request Quote for This Date'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {date && isDateBooked(date) && (
            <div className="w-full">
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <p className="text-center font-semibold text-amber-800 mb-2">
                    ⚠ {date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} is currently booked
                  </p>
                  <p className="text-center text-sm text-amber-700 mb-4">
                    We may still be able to accommodate you. Submit a request and we'll be in touch.
                  </p>
                  <Button 
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    disabled={submitting}
                    onClick={async () => {
                      setSubmitting(true);
                      try {
                        const bookingData = {
                          selectedDate: date.toISOString().split('T')[0],
                          isBooked: true
                        };
                        await axios.post(`${API}/bookings`, bookingData);
                        toast({
                          title: "Request Submitted!",
                          description: "We'll check our availability and contact you shortly.",
                        });
                      } catch (error) {
                        console.error('Error submitting booking:', error);
                        toast({
                          title: "Error",
                          description: "Failed to submit request. Please try again.",
                          variant: "destructive"
                        });
                      }
                      setSubmitting(false);
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Request Anyway'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingCalendar;